import React from 'react';
import { Attachment } from '@interfaces/SurgeryRequest/Attachment';
import { useGetSignedUrlForUpload } from '@data/documentSignedUrl/hooks/useGetSignedUrlForUpload/useGetSignedUrlForUpload';
import { useUploadDocumentWithSignedUrl } from '@data/documentSignedUrl/hooks/useUploadDocumentWithSignedUrl/useUploadDocumentWithSignedUrl';
import { useDispatch } from 'react-redux';
import { saveFormError } from '@store/actions/RequestFormActionCreators';
import { GENERIC_SERVICE_ERROR } from '@components/RequestForm/FormSubmitSection/FormSubmitSection';
import { DocumentRecord } from '@components/RequestForm/DocumentRequest/DocumentRequest';

export type AttachmentPayload = Pick<Attachment, 'file' | 'id'>;
interface UseUploadSurgeryRequestDocumentsReturn {
	uploadDocs: (
		documents: DocumentRecord,
		onSuccessCb: (attachmentPayload: AttachmentPayload[]) => void,
	) => void;
}

export const useUploadDocumentsAndSubmitWithPayload = (unitId: string): UseUploadSurgeryRequestDocumentsReturn => {
	const getSignedUrlForUpload = useGetSignedUrlForUpload(unitId);
	const uploadDocumentWithSignedUrl = useUploadDocumentWithSignedUrl();
	const dispatch = useDispatch();

	// Track uploads by document key
	const uploadedDocsRef = React.useRef<Record<string, AttachmentPayload | null>>({});

	const uploadDocs = React.useCallback((
		documents: DocumentRecord,
		onSuccessCb: (attachmentPayload: AttachmentPayload[]) => void
	) => {
		// Creates new tracking object that:
		//  - Keeps successful uploads from previous attempts (uploadedDocsRef.current[key])
		//  - Sets null for new/failed documents that need (re)upload
		// Removes tracking for documents no longer in the input
		uploadedDocsRef.current = Object.keys(documents).reduce<
			Record<string, AttachmentPayload | null>
		>((acc, key) => {
			acc[key] = uploadedDocsRef.current[key] || null;
			return acc;
		}, {});

		const uploadPromises = Object.entries(documents).map(([key, doc]) =>
			new Promise<void>((resolve) => {
				// Skip if already uploaded
				if (uploadedDocsRef.current[key]) {
					resolve();
					return;
				}

				getSignedUrlForUpload(doc, {
					onSuccess: ({ data: { signedUrl, metadata } }) => {
						uploadDocumentWithSignedUrl(signedUrl, doc, metadata.id, {
							onSuccess: () => {
								uploadedDocsRef.current[key] = {
									id: metadata.id,
									file: metadata.fileName
								};
								resolve();
							},
							onFailure: () => {
								uploadedDocsRef.current[key] = null;
								resolve();
							}
						});
					},
					onFailure: () => {
						uploadedDocsRef.current[key] = null;
						resolve();
					}
				});
			})
		);

		Promise.all(uploadPromises)
			.then(() => {
				// Check if any uploads failed
				const hasFailedUploads = Object.values(uploadedDocsRef.current).some(doc => doc === null);
				if (hasFailedUploads) {
					dispatch(saveFormError(GENERIC_SERVICE_ERROR));
					return;
				}

				const attachmentPayload: AttachmentPayload[] = Object.values(uploadedDocsRef.current).filter(doc => doc !== null);
				onSuccessCb(attachmentPayload);
			})
			.catch(() => null);
	}, [dispatch, getSignedUrlForUpload, uploadDocumentWithSignedUrl]);

	return { uploadDocs };
};
